import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Edit, Save, X, FileText, Info, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Human-friendly labels for known sections
const SECTION_META = {
  about_intro:      { label: 'About Us — Introduction',        page: '/about',                helper: 'Main introduction paragraph on the About page. Separate paragraphs with a blank line.' },
  what_we_offer:    { label: 'About Us — What We Offer',       page: '/about',                helper: 'Bullet list on the About page. Separate items with the pipe character "|".' },
  why_choose_us:    { label: 'About Us — Why Choose Us',       page: '/about',                helper: 'Long-form text on the About page. Blank line separates paragraphs.' },
  our_vision:       { label: 'About Us — Our Vision',          page: '/about',                helper: 'Closing statement on the About page. Blank line separates paragraphs.' },
  terms_full:       { label: 'Terms & Conditions (full page)', page: '/terms-and-conditions', helper: 'Full Terms & Conditions in Markdown. Use "##" for section headings, "-" for bullets, ">" for highlighted callouts.' },
  cancellation_full:{ label: 'Cancellation Policy (full page)',page: '/cancellation-policy',  helper: 'Full Cancellation Policy in Markdown. Use "##" for section headings, "-" for bullets.' },
};

const AdminSiteContent = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // section id being edited
  const [form, setForm] = useState({ title: '', content: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/site-content`);
      // Sort by order, then by section key for stable UI
      const sorted = [...res.data].sort((a, b) => (a.order || 0) - (b.order || 0) || a.section.localeCompare(b.section));
      setSections(sorted);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load site content');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (s) => {
    setEditing(s.id);
    setForm({ title: s.title || '', content: s.content || '' });
  };

  const closeEdit = () => {
    setEditing(null);
    setForm({ title: '', content: '' });
  };

  const handleSave = async () => {
    const current = sections.find((s) => s.id === editing);
    if (!current) return;
    if (!form.title.trim() || !form.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API}/admin/site-content/${editing}`,
        {
          section: current.section,
          title: form.title.trim(),
          content: form.content,
          order: current.order || 0,
          is_active: current.is_active !== false,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Content saved!');
      closeEdit();
      fetchAll();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="font-body text-muted-foreground">Loading site content...</p>
      </div>
    );
  }

  const currentMeta = editing ? SECTION_META[sections.find((s) => s.id === editing)?.section] : null;

  return (
    <div data-testid="admin-site-content">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl text-navy">Site Content</h1>
          <p className="font-body text-sm text-muted-foreground mt-1">
            Edit About Us sections, Terms & Conditions, and Cancellation Policy.
          </p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start space-x-3">
        <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="font-body text-sm text-blue-900 leading-relaxed">
          <strong>Tip:</strong> Terms & Conditions and Cancellation Policy support Markdown —
          use <code className="bg-white px-1 rounded">## Heading</code>, <code className="bg-white px-1 rounded">- bullet</code>,{' '}
          <code className="bg-white px-1 rounded">**bold**</code>, and <code className="bg-white px-1 rounded">&gt; callout</code> lines.
          Everything else uses plain paragraphs (separate them with a blank line).
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="text-left font-body text-sm font-semibold text-navy px-6 py-4">Section</th>
              <th className="text-left font-body text-sm font-semibold text-navy px-6 py-4">Preview page</th>
              <th className="text-left font-body text-sm font-semibold text-navy px-6 py-4">Title</th>
              <th className="text-left font-body text-sm font-semibold text-navy px-6 py-4">Length</th>
              <th className="text-right font-body text-sm font-semibold text-navy px-6 py-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sections.map((s, index) => {
              const meta = SECTION_META[s.section];
              return (
                <tr key={s.id} className={index % 2 === 0 ? 'bg-white' : 'bg-sand/30'}>
                  <td className="px-6 py-4">
                    <p className="font-body font-semibold text-navy">
                      {meta?.label || s.section}
                    </p>
                    <p className="font-body text-xs text-muted-foreground mt-0.5">
                      key: <code className="bg-muted px-1 rounded">{s.section}</code>
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    {meta?.page ? (
                      <a
                        href={meta.page}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-body text-sm text-emerald-700 underline hover:text-emerald-900"
                        data-testid={`preview-${s.section}`}
                      >
                        {meta.page}
                      </a>
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </td>
                  <td className="font-body px-6 py-4 text-sm">{s.title}</td>
                  <td className="font-body px-6 py-4 text-sm text-muted-foreground">
                    {(s.content || '').length.toLocaleString()} chars
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => openEdit(s)}
                        className="p-2 hover:bg-muted rounded-lg transition-colors flex items-center space-x-1"
                        title="Edit"
                        data-testid={`edit-section-${s.section}`}
                      >
                        <Edit size={18} className="text-navy" />
                        <span className="font-body text-sm text-navy">Edit</span>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 bg-emerald-700 p-6 flex items-center justify-between z-10">
              <div className="flex items-center space-x-3">
                <FileText size={24} className="text-amber-300" />
                <h2 className="font-heading text-2xl text-amber-300">
                  {currentMeta?.label || 'Edit Section'}
                </h2>
              </div>
              <button
                onClick={closeEdit}
                className="text-white hover:text-amber-300"
                data-testid="close-content-edit-modal"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {currentMeta?.helper && (
                <div className="bg-amber-50 border-l-4 border-amber-400 p-4 flex items-start space-x-3">
                  <AlertCircle size={18} className="text-amber-700 flex-shrink-0 mt-0.5" />
                  <p className="font-body text-sm text-amber-900 leading-relaxed">
                    {currentMeta.helper}
                  </p>
                </div>
              )}

              <div>
                <label className="block font-body text-sm font-medium text-navy mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full border border-border rounded-lg px-4 py-2 font-body focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  data-testid="content-title-input"
                />
              </div>

              <div>
                <label className="block font-body text-sm font-medium text-navy mb-2">
                  Content *
                  <span className="text-xs text-muted-foreground font-normal ml-2">
                    ({form.content.length.toLocaleString()} characters)
                  </span>
                </label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  rows={20}
                  className="w-full border border-border rounded-lg px-4 py-3 font-mono text-sm resize-y focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  data-testid="content-body-input"
                />
              </div>

              <div className="flex space-x-4 pt-2">
                <button
                  onClick={closeEdit}
                  className="flex-1 bg-muted text-navy px-6 py-3 rounded-lg font-body font-semibold hover:bg-gray-200 transition"
                  data-testid="cancel-content-edit-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-emerald-600 text-white px-6 py-3 rounded-lg font-body font-semibold hover:bg-emerald-700 flex items-center justify-center space-x-2 transition shadow-md disabled:opacity-60"
                  data-testid="save-content-btn"
                >
                  <Save size={20} />
                  <span>{saving ? 'Saving…' : 'Save Changes'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSiteContent;
